import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box,
  Button,
  CardMedia,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../utils/config';
import { getImageSrc } from '../../utils/imageHelper';
import { FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { COLORS, HOVER_SCALE_IMG_SX } from '../../theme/tokens';

const ReviewList = ({ productId, hideWhenEmpty = false, title, headingLevel = 'h2' }) => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const lastReviewElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreReviews();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);
  const fetchReviews = async (pageNum = 1, append = false) => {
    try {
      const loadingState = append ? setLoadingMore : setLoading;
      loadingState(true);
      const response = await axios.get(productId
        ? `${API_URL}/reviews/product/${productId}`
        : `${API_URL}/reviews?page=${pageNum}&limit=9`);
      if (response.data.success) {
        if (productId) {
          setReviews(response.data.reviews || []);
          setHasMore(false);
          return;
        }

        if (append) {
          setReviews(prev => [...prev, ...response.data.reviews]);
        } else {
          setReviews(response.data.reviews);
        }
        setHasMore(response.data.pagination.hasMore);
      } else {
        setError(t('reviews.loadFailed'));
      }
    } catch (err) {
      setError('Error fetching reviews: ' + err.message);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };
  const loadMoreReviews = () => {
    if (!hasMore || loadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReviews(nextPage, true);
  };
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);
  // Function to render stars based on rating
  const renderStars = (rating) => {
    return (
      <Box component="span" aria-label={`${rating} out of 5 stars`} role="img">
        {Array(5).fill(0).map((_, i) => (
          <FaStar
            key={i}
            color={i < rating ? '#ffc107' : '#e4e5e9'}
            style={{ marginRight: '2px' }}
            aria-hidden="true"
          />
        ))}
      </Box>
    );
  };
  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Function to parse product title from JSON string
  const getProductTitle = (titleJson) => {
    if (!titleJson) return '';

    try {
      if (typeof titleJson === 'object') {
        return titleJson.EN || Object.values(titleJson)[0] || '';
      }
      const titles = JSON.parse(titleJson);
      return titles.EN || Object.values(titles)[0] || '';
    } catch (e) {
      // If parsing fails, return the original string
      return titleJson || '';
    }
  };
  const renderProductSummary = (product) => {
    if (!product) return null;

    const productTitle = String(getProductTitle(product.product_title) || '').trim();
    const productSlug = typeof product.product_slug === 'string'
      ? product.product_slug.trim()
      : '';
    const productImage = typeof product.product_image === 'string'
      ? product.product_image.trim()
      : '';

    if (!productTitle) return null;

    const summary = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {productImage && (
          <Box sx={{
            width: '80px',
            height: '80px',
            position: 'relative',
            borderRadius: '2px',
            overflow: 'hidden',
            backgroundColor: COLORS.surfacePanel,
            ...HOVER_SCALE_IMG_SX,
          }}>
            <CardMedia
              component="img"
              image={getImageSrc(productImage, 'thumbs')}
              alt={productTitle}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${productImage}`;
              }}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}
        <Typography color="primary" variant="body1" sx={{ fontWeight: 'medium' }}>
          {productTitle}
        </Typography>
      </Box>
    );

    return (
      <Box mt={2} pt={2} borderTop={1} borderColor="divider">
        {productSlug ? (
          <Link
            to={`/product/${productSlug}`}
            aria-label={productTitle}
            style={{ textDecoration: 'none' }}
          >
            {summary}
          </Link>
        ) : summary}
      </Box>
    );
  };
  if (loading && reviews.length === 0) {
    if (hideWhenEmpty) {
      return null;
    }

    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }
  if (error && reviews.length === 0) {
    if (hideWhenEmpty) {
      return null;
    }

    return (
      <Box display="flex" justifyContent="center" my={5}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (reviews.length === 0 && !loading) {
    if (hideWhenEmpty) {
      return null;
    }

    return (
      <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
        <Typography variant="h3" component={headingLevel} align="center" gutterBottom sx={{ mb: { xs: 4, md: 6 }, fontFamily: 'Playfair Display, serif', fontWeight: 600 }}>
          {title || t('reviews.title')}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Typography>{t('reviews.none')}</Typography>
        </Box>
      </Container>
    );
  }
  return (
    <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
      <Typography variant="h3" component={headingLevel} align="center" gutterBottom sx={{ mb: { xs: 4, md: 6 }, fontFamily: 'Playfair Display, serif', fontWeight: 600 }}>
        {title || t('reviews.title')}
      </Typography>
      <Grid container spacing={3}>
        {reviews.map((review, index) => {
          const isLastElement = index === reviews.length - 1;
          return (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={6}
              key={review.review_id}
              ref={isLastElement ? lastReviewElementRef : null}
            >
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '2px', borderColor: COLORS.borderHairline, boxShadow: 'none' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Box display="flex">
                      {renderStars(review.rating)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(review.review_created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {review.author_name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      height: '80px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      mb: 2
                    }}
                  >
                    {review.comment_content}
                  </Typography>
                  {renderProductSummary(review.product)}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {loadingMore && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress size={30} />
        </Box>
      )}
      {!productId && !hasMore && reviews.length > 0 && (
        <Box display="flex" justifyContent="center" my={3}>
          <Typography variant="body2" color="text.secondary">
            {t('reviews.noMore')}
          </Typography>
        </Box>
      )}
      {hasMore && !loading && !loadingMore && reviews.length > 0 && (
        <Box display="flex" justifyContent="center" my={3}>
          <Button 
            variant="outlined" 
            onClick={loadMoreReviews}
            disabled={loadingMore}
          >
            {t('reviews.loadMore')}
          </Button>
        </Box>
      )}
    </Container>
  );
};
export default ReviewList;
